import type {AxiosError, AxiosStatic, InternalAxiosRequestConfig} from 'axios'
import {GET_ACCESSTOKEN_WITH_TOKEN_URL, refreshToken} from './_requests'
import {AuthModel} from './_models'

const AUTH_LOCAL_STORAGE_KEY = 'kt-auth-react-v'
const AUTH_ROLES_LOCAL_STORAGE_KEY = 'kt-auth-roles-react-v'
const AUTH_SESSION_STARTED_AT_KEY = 'cbee-auth-session-started-at'
const PUBLIC_ROUTES = ['/program']

interface AuthRolesModel {
  roles: AuthModel[]
  activeRoleIndex: number
}

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

let refreshRequest: ReturnType<typeof refreshToken> | null = null

const getStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

const getAuthRoles = (): AuthRolesModel | undefined => {
  const storage = getStorage()
  const storedValue = storage?.getItem(AUTH_ROLES_LOCAL_STORAGE_KEY)
  if (!storedValue) {
    return undefined
  }

  try {
    const authRoles = JSON.parse(storedValue) as AuthRolesModel
    return Array.isArray(authRoles.roles) ? authRoles : undefined
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE PARSE ERROR', error)
    return undefined
  }
}

const setAuthRoles = (authRoles: AuthRolesModel) => {
  const storage = getStorage()
  if (!storage) {
    return
  }

  try {
    storage.setItem(AUTH_ROLES_LOCAL_STORAGE_KEY, JSON.stringify(authRoles))
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE SAVE ERROR', error)
  }
}

const setActiveRole = (index: number) => {
  const authRoles = getAuthRoles()
  if (authRoles && index >= 0 && index < authRoles.roles.length) {
    setAuthRoles({...authRoles, activeRoleIndex: index})
  }
}

const getActiveAuth = (): AuthModel | undefined => {
  const authRoles = getAuthRoles()
  if (!authRoles || authRoles.roles.length === 0) {
    return undefined
  }

  return authRoles.roles[authRoles.activeRoleIndex] ?? authRoles.roles[0]
}

const removeAuthRoles = () => {
  const storage = getStorage()
  if (!storage) {
    return
  }

  try {
    storage.removeItem(AUTH_ROLES_LOCAL_STORAGE_KEY)
    storage.removeItem(AUTH_SESSION_STARTED_AT_KEY)
  } catch (error) {
    console.error('AUTH ROLES LOCAL STORAGE REMOVE ERROR', error)
  }
}

const getAuthSessionStartedAt = (): number | undefined => {
  const storedValue = getStorage()?.getItem(AUTH_SESSION_STARTED_AT_KEY)
  if (!storedValue) {
    return undefined
  }

  const parsedValue = Number(storedValue)
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : undefined
}

const ensureAuthSessionStartedAt = (reset = false): number | undefined => {
  const storage = getStorage()
  if (!storage) {
    return undefined
  }

  const existingValue = getAuthSessionStartedAt()
  if (existingValue && !reset) {
    return existingValue
  }

  const nextValue = Date.now()
  storage.setItem(AUTH_SESSION_STARTED_AT_KEY, String(nextValue))
  return nextValue
}

const clearSessionAndRedirect = () => {
  removeAuthRoles()
  if (window.location.pathname !== '/auth/login') {
    window.location.href = '/auth/login'
  }
}

export function setupAxios(client: AxiosStatic) {
  client.defaults.headers.common.Accept = 'application/json'
  client.interceptors.request.use(
    (config) => {
      const auth = getActiveAuth()
      if (auth?.token && !config.headers.has('Authorization')) {
        config.headers.set('Authorization', `Bearer ${auth.token}`)
      }

      return config
    },
    (error: unknown) => Promise.reject(error)
  )

  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const {response} = error
      if (response?.status === 403) {
        const responseData = response.data as
          | string
          | {message?: unknown; Message?: unknown}
          | undefined
        const rawMessage =
          typeof responseData === 'string'
            ? responseData
            : responseData?.message ?? responseData?.Message
        window.alert(
          typeof rawMessage === 'string' && rawMessage.trim()
            ? rawMessage
            : 'Bu işlem için yetkiniz bulunmuyor'
        )

        return Promise.reject(error)
      }

      if (response?.status !== 401) {
        return Promise.reject(error)
      }

      const currentPath = window.location.pathname
      if (PUBLIC_ROUTES.some((route) => currentPath.startsWith(route))) {
        return Promise.reject(error)
      }

      const requestConfig = error.config as RetryableRequestConfig | undefined
      if (
        !requestConfig ||
        requestConfig._retry ||
        requestConfig.url === GET_ACCESSTOKEN_WITH_TOKEN_URL
      ) {
        clearSessionAndRedirect()
        return Promise.reject(error)
      }

      const auth = getActiveAuth()
      if (!auth?.refreshToken) {
        clearSessionAndRedirect()
        return Promise.reject(error)
      }

      try {
        requestConfig._retry = true
        refreshRequest ??= refreshToken(auth.refreshToken)
        const {data: authResponse} = await refreshRequest
        const firstRoleAuth = authResponse[0]
        if (!firstRoleAuth?.token) {
          throw new Error('Token refresh did not return a usable token.')
        }

        setAuthRoles({roles: authResponse, activeRoleIndex: 0})
        requestConfig.headers.set('Authorization', `Bearer ${firstRoleAuth.token}`)
        return client(requestConfig)
      } catch {
        clearSessionAndRedirect()
        return Promise.reject(error)
      } finally {
        refreshRequest = null
      }
    }
  )
}

export {
  getAuthRoles,
  setAuthRoles,
  setActiveRole,
  getActiveAuth,
  removeAuthRoles,
  getAuthSessionStartedAt,
  ensureAuthSessionStartedAt,
  AUTH_LOCAL_STORAGE_KEY,
}
